import React, { useState, useMemo } from 'react';
import {
  Briefcase,
  Search,
  Filter,
  MapPin,
  Calendar,
  DollarSign,
  Sparkles,
  Eye,
  Flag,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  User,
  Clock,
  ShieldCheck,
  UserCheck,
  RotateCcw,
  RefreshCw,
  ArrowRight,
  Send,
  Phone,
  Mail,
  Building2,
} from 'lucide-react';

interface AdminJobSectionProps {
  jobs: any[];
  professionals?: any[];
  onAction: (
    jobId: string,
    action: 'flag' | 'unflag' | 'cancel' | 'update_status' | 'assign' | 'reassign' | 'unassign',
    reason?: string,
    adminNotes?: string,
    newStatus?: string,
    professionalId?: string
  ) => Promise<void>;
  isProcessing: boolean;
  onRefresh?: () => Promise<void>;
}

export type PipelineFilterStage =
  | 'all'
  | 'open'
  | 'assigned'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'flagged';

export const AdminJobSection: React.FC<AdminJobSectionProps> = ({
  jobs,
  professionals = [],
  onAction,
  isProcessing,
  onRefresh,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pipelineStage, setPipelineStage] = useState<PipelineFilterStage>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [assignedProFilter, setAssignedProFilter] = useState('all');
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [actionModal, setActionModal] = useState<{
    isOpen: boolean;
    job: any | null;
    action: 'flag' | 'unflag' | 'cancel' | 'update_status' | 'assign' | 'reassign' | 'unassign';
  }>({
    isOpen: false,
    job: null,
    action: 'flag',
  });
  const [actionReason, setActionReason] = useState('');
  const [newStatus, setNewStatus] = useState('in_progress');
  const [selectedProId, setSelectedProId] = useState('');

  // Categories list
  const categories = useMemo(() => {
    const set = new Set<string>();
    jobs.forEach((j) => {
      const cat = j.category_slug || j.category;
      if (cat) set.add(cat);
    });
    return Array.from(set);
  }, [jobs]);

  // Verified Professionals for Reassignment Dropdown
  const eligibleProsForAssignment = useMemo(() => {
    return professionals.filter(
      (p) =>
        p.role === 'professional' &&
        !p.is_suspended &&
        !p.isSuspended &&
        (p.verification_status === 'verified' || p.verificationStatus === 'verified')
    );
  }, [professionals]);

  // Unique Assigned Professionals in Current Job Set
  const assignedProfessionalsInJobs = useMemo(() => {
    const map = new Map<string, string>();
    jobs.forEach((j) => {
      const proId =
        j.assignedProfessionalId ||
        j.selectedProfessionalId ||
        j.selected_professional_id ||
        j.assigned_professional_id;
      let proName = j.assignedProfessionalName || j.selectedProfessionalName;
      if (!proName && proId && professionals) {
        const found = professionals.find((p) => p.id === proId);
        if (found) {
          proName = found.fullName || found.businessName || (found as any).full_name;
        }
      }
      if (proId && proName) {
        map.set(proId, proName);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [jobs, professionals]);

  // Comprehensive Pipeline Counts
  const counts = useMemo(() => {
    let open = 0;
    let assigned = 0;
    let inProgress = 0;
    let submitted = 0;
    let completed = 0;
    let cancelled = 0;
    let flagged = 0;

    jobs.forEach((j) => {
      const st = (j.status || '').toLowerCase();
      const pStage = j.pipelineStage;
      const isAssigned =
        Boolean(
          j.assignedProfessionalId ||
          j.selectedProfessionalId ||
          j.selected_professional_id ||
          j.assigned_professional_id ||
          j.isAssigned
        ) ||
        st === 'assigned' ||
        st === 'in_progress' ||
        st === 'accepted' ||
        st === 'professional_completed' ||
        st === 'completed_by_professional' ||
        pStage === 'assigned' ||
        pStage === 'in_progress' ||
        pStage === 'submitted';

      if (j.isFlagged) flagged++;

      if (st === 'completed' || pStage === 'completed') {
        completed++;
      } else if (st === 'cancelled' || pStage === 'cancelled') {
        cancelled++;
      } else if (isAssigned) {
        assigned++;
        if (st === 'in_progress' || pStage === 'in_progress') inProgress++;
        if (
          st === 'professional_completed' ||
          st === 'completed_by_professional' ||
          pStage === 'submitted'
        ) {
          submitted++;
        }
      } else {
        open++;
      }
    });

    return {
      total: jobs.length,
      open,
      assigned,
      inProgress,
      submitted,
      completed,
      cancelled,
      flagged,
    };
  }, [jobs]);

  // Rigorous Filtering Logic
  const filteredJobs = useMemo(() => {
    return jobs.filter((j) => {
      const title = (j.title || '').toLowerCase();
      const desc = (j.description || '').toLowerCase();
      const cat = (j.category_slug || j.category || '').toLowerCase();
      const st = (j.status || '').toLowerCase();
      const cust = (j.customerName || j.customer_name || '').toLowerCase();
      const proId =
        j.assignedProfessionalId ||
        j.selectedProfessionalId ||
        j.selected_professional_id ||
        j.assigned_professional_id;
      let proName = (j.assignedProfessionalName || j.selectedProfessionalName || '').toLowerCase();
      if (!proName && proId && professionals) {
        const found = professionals.find((p) => p.id === proId);
        if (found) {
          proName = (found.fullName || found.businessName || (found as any).full_name || '').toLowerCase();
        }
      }
      const term = searchTerm.toLowerCase();

      const matchesSearch =
        !searchTerm ||
        title.includes(term) ||
        desc.includes(term) ||
        cust.includes(term) ||
        proName.includes(term) ||
        j.id.toLowerCase().includes(term);

      // Pipeline Stage Matching
      const isAssignedJob =
        Boolean(
          j.assignedProfessionalId ||
          j.selectedProfessionalId ||
          j.selected_professional_id ||
          j.assigned_professional_id ||
          j.isAssigned
        ) ||
        st === 'assigned' ||
        st === 'in_progress' ||
        st === 'accepted' ||
        st === 'professional_completed' ||
        st === 'completed_by_professional' ||
        j.pipelineStage === 'assigned' ||
        j.pipelineStage === 'in_progress' ||
        j.pipelineStage === 'submitted';

      let matchesStage = true;
      if (pipelineStage === 'open') {
        matchesStage = (st === 'open' || j.pipelineStage === 'open') && !isAssignedJob;
      } else if (pipelineStage === 'assigned') {
        matchesStage = isAssignedJob && st !== 'completed' && st !== 'cancelled';
      } else if (pipelineStage === 'in_progress') {
        matchesStage =
          (st === 'in_progress' ||
            st === 'professional_completed' ||
            st === 'completed_by_professional' ||
            j.pipelineStage === 'in_progress' ||
            j.pipelineStage === 'submitted') &&
          st !== 'completed';
      } else if (pipelineStage === 'completed') {
        matchesStage = st === 'completed' || j.pipelineStage === 'completed';
      } else if (pipelineStage === 'cancelled') {
        matchesStage = st === 'cancelled' || j.pipelineStage === 'cancelled';
      } else if (pipelineStage === 'flagged') {
        matchesStage = Boolean(j.isFlagged);
      }

      const matchesCategory =
        categoryFilter === 'all' || cat === categoryFilter.toLowerCase();

      const matchesUrgency =
        urgencyFilter === 'all' ||
        (j.urgency || '').toLowerCase() === urgencyFilter.toLowerCase();

      const matchesPro =
        assignedProFilter === 'all' ||
        proId === assignedProFilter;

      return (
        matchesSearch &&
        matchesStage &&
        matchesCategory &&
        matchesUrgency &&
        matchesPro
      );
    });
  }, [
    jobs,
    professionals,
    searchTerm,
    pipelineStage,
    categoryFilter,
    urgencyFilter,
    assignedProFilter,
  ]);

  const handleOpenActionModal = (
    job: any,
    action: 'flag' | 'unflag' | 'cancel' | 'update_status' | 'assign' | 'reassign' | 'unassign'
  ) => {
    setActionModal({ isOpen: true, job, action });
    setSelectedProId(job.assignedProfessionalId || job.selectedProfessionalId || '');

    if (action === 'flag') {
      setActionReason('Flagged for suspicious description or pricing scope inquiry.');
    } else if (action === 'cancel') {
      setActionReason('Cancelled by platform admin due to customer request or dispute.');
    } else if (action === 'unflag') {
      setActionReason('Job review cleared by administrator.');
    } else if (action === 'reassign' || action === 'assign') {
      setActionReason('Reassigned by platform admin to ensure SLA delivery.');
    } else if (action === 'unassign') {
      setActionReason('Assignment revoked by platform admin, returned to open pool.');
    } else {
      setActionReason('Status manually updated.');
      setNewStatus(job.status || 'in_progress');
    }
  };

  const handleConfirmAction = async () => {
    if (!actionModal.job) return;
    try {
      await onAction(
        actionModal.job.id,
        actionModal.action,
        actionReason,
        actionReason,
        actionModal.action === 'update_status' ? newStatus : undefined,
        (actionModal.action === 'assign' || actionModal.action === 'reassign')
          ? selectedProId
          : undefined
      );
      setActionModal({ isOpen: false, job: null, action: 'flag' });
      if (selectedJob?.id === actionModal.job.id) {
        setSelectedJob(null);
      }
    } catch (err) {
      console.error('Error confirming action:', err);
    }
  };

  const handleManualRefresh = async () => {
    if (onRefresh) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  return (
    <div id="admin-jobs-section" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900 border border-slate-800 p-5 rounded-3xl shadow-xl">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-amber-400" />
              <span>Job Matching & Pipeline Management ({jobs.length})</span>
            </h2>
            {onRefresh && (
              <button
                onClick={handleManualRefresh}
                disabled={isRefreshing || isProcessing}
                className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors text-xs flex items-center gap-1"
                title="Refresh jobs from Supabase"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-emerald-400' : ''}`} />
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Authoritative oversight of customer postings, algorithmic & proximity matching, formal assignments, and live in-progress job execution.
          </p>
        </div>

        {/* Live Operational Metrics Badges */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="px-3 py-1.5 rounded-xl bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 font-semibold flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>{counts.open} Open</span>
          </span>
          <span className="px-3 py-1.5 rounded-xl bg-amber-500/10 text-amber-300 border border-amber-500/20 font-bold flex items-center gap-1.5">
            <UserCheck className="w-3.5 h-3.5 text-amber-400" />
            <span>{counts.assigned} Assigned / In Progress</span>
          </span>
          <span className="px-3 py-1.5 rounded-xl bg-purple-500/10 text-purple-300 border border-purple-500/20 font-semibold">
            {counts.completed} Completed
          </span>
        </div>
      </div>

      {/* Pipeline Stage Tabs */}
      <div className="bg-slate-900 border border-slate-800 p-1.5 rounded-2xl flex flex-wrap gap-1 shadow-md">
        <button
          onClick={() => setPipelineStage('all')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
            pipelineStage === 'all'
              ? 'bg-slate-800 text-white shadow-xs'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-850'
          }`}
        >
          <span>All Jobs</span>
          <span className="px-1.5 py-0.2 rounded-full bg-slate-950 text-slate-400 text-[10px]">
            {counts.total}
          </span>
        </button>

        <button
          onClick={() => setPipelineStage('open')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
            pipelineStage === 'open'
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-850'
          }`}
        >
          <span>Open for Quotes</span>
          <span className="px-1.5 py-0.2 rounded-full bg-emerald-500/30 text-emerald-200 text-[10px]">
            {counts.open}
          </span>
        </button>

        <button
          onClick={() => setPipelineStage('assigned')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
            pipelineStage === 'assigned'
              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-850'
          }`}
        >
          <UserCheck className="w-3.5 h-3.5 text-amber-400" />
          <span>Assigned / In Progress</span>
          <span className="px-1.5 py-0.2 rounded-full bg-amber-500/30 text-amber-200 text-[10px]">
            {counts.assigned}
          </span>
        </button>

        <button
          onClick={() => setPipelineStage('completed')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
            pipelineStage === 'completed'
              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-850'
          }`}
        >
          <span>Completed</span>
          <span className="px-1.5 py-0.2 rounded-full bg-purple-500/30 text-purple-200 text-[10px]">
            {counts.completed}
          </span>
        </button>

        <button
          onClick={() => setPipelineStage('cancelled')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
            pipelineStage === 'cancelled'
              ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-850'
          }`}
        >
          <span>Cancelled</span>
          <span className="px-1.5 py-0.2 rounded-full bg-rose-500/30 text-rose-200 text-[10px]">
            {counts.cancelled}
          </span>
        </button>

        <button
          onClick={() => setPipelineStage('flagged')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
            pipelineStage === 'flagged'
              ? 'bg-amber-600/20 text-amber-400 border border-amber-600/30'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-850'
          }`}
        >
          <Flag className="w-3.5 h-3.5" />
          <span>Flagged</span>
          {counts.flagged > 0 && (
            <span className="px-1.5 py-0.2 rounded-full bg-amber-500 text-slate-950 font-bold text-[10px]">
              {counts.flagged}
            </span>
          )}
        </button>
      </div>

      {/* Filters Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search title, ID, customer, pro..."
            className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
          />
        </div>

        {/* Category Filter */}
        <div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none focus:border-amber-500"
          >
            <option value="all">All Service Categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1).replace('-', ' ')}
              </option>
            ))}
          </select>
        </div>

        {/* Assigned Professional Filter */}
        <div>
          <select
            value={assignedProFilter}
            onChange={(e) => setAssignedProFilter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none focus:border-amber-500"
          >
            <option value="all">All Assigned Artisans</option>
            {assignedProfessionalsInJobs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {/* Urgency Filter */}
        <div>
          <select
            value={urgencyFilter}
            onChange={(e) => setUrgencyFilter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none focus:border-amber-500"
          >
            <option value="all">All Urgency Levels</option>
            <option value="emergency">🚨 Emergency (Within 2 hrs)</option>
            <option value="within_48h">⚡ Within 48 Hours</option>
            <option value="flexible">🗓️ Flexible</option>
          </select>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950/80 border-b border-slate-800 text-[11px] uppercase font-bold text-slate-400 tracking-wider">
              <tr>
                <th className="py-3.5 px-4">Job Title & Customer</th>
                <th className="py-3.5 px-4">Trade & AI Scope</th>
                <th className="py-3.5 px-4">Location & Budget</th>
                <th className="py-3.5 px-4">Quotes / Assigned Professional</th>
                <th className="py-3.5 px-4">Pipeline Status</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {filteredJobs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-slate-500">
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <Briefcase className="w-8 h-8 text-slate-600 mb-1" />
                      <p className="font-semibold text-slate-400 text-sm">
                        No jobs found in "{pipelineStage.replace('_', ' ').toUpperCase()}" stage
                      </p>
                      <p className="text-xs text-slate-500 max-w-sm">
                        No jobs currently match your search and filter criteria. Check another pipeline stage or clear filters.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredJobs.map((job) => {
                  const isFlagged = Boolean(job.isFlagged);
                  const proId =
                    job.assignedProfessionalId ||
                    job.selectedProfessionalId ||
                    job.selected_professional_id ||
                    job.assigned_professional_id;
                  const isAssigned = Boolean(
                    proId ||
                    job.isAssigned ||
                    job.status === 'assigned' ||
                    job.status === 'in_progress' ||
                    job.status === 'accepted' ||
                    job.status === 'professional_completed' ||
                    job.status === 'completed_by_professional' ||
                    job.pipelineStage === 'assigned' ||
                    job.pipelineStage === 'in_progress' ||
                    job.pipelineStage === 'submitted'
                  );
                  let assignedName = job.assignedProfessionalName || job.selectedProfessionalName;
                  if (!assignedName && proId && professionals) {
                    const found = professionals.find((p) => p.id === proId);
                    if (found) {
                      assignedName = found.fullName || found.businessName || (found as any).full_name;
                    }
                  }

                  return (
                    <tr
                      key={job.id}
                      className={`hover:bg-slate-800/40 transition-colors ${
                        isFlagged ? 'bg-amber-950/10' : isAssigned ? 'bg-blue-950/5' : ''
                      }`}
                    >
                      {/* Title & Customer */}
                      <td className="py-3.5 px-4 max-w-xs">
                        <div>
                          <div className="font-bold text-white flex items-center gap-1.5">
                            {isFlagged && <Flag className="w-3.5 h-3.5 text-amber-400 fill-amber-400 shrink-0" />}
                            <span className="truncate">{job.title}</span>
                          </div>
                          <div className="text-[11px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                            <span className="text-slate-300 font-medium">{job.customerName || 'Customer'}</span>
                            <span>•</span>
                            <span className="font-mono text-[10px]">ID: {job.id.slice(0, 8)}...</span>
                          </div>
                        </div>
                      </td>

                      {/* Trade & AI Scope */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1">
                          <span className="inline-block px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 font-semibold capitalize text-[11px] border border-emerald-500/20">
                            {job.category_slug || job.category || 'General Repair'}
                          </span>
                          {job.aiAnalysis?.detected_category && (
                            <div className="text-[10px] text-slate-400 flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-emerald-400 shrink-0" />
                              <span className="truncate">AI: {job.aiAnalysis.detected_category}</span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Location & Budget */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-0.5">
                          <div className="text-[11px] text-slate-300 flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-slate-500 shrink-0" />
                            <span>{job.lga ? `${job.lga}, ${job.state}` : job.state || 'Lagos'}</span>
                          </div>
                          <div className="text-[11px] text-emerald-400 font-mono font-medium">
                            {job.budgetEstimate || job.budget_estimate || job.budget_naira
                              ? `₦${Number(job.budgetEstimate || job.budget_estimate || job.budget_naira).toLocaleString()}`
                              : 'Open Budget'}
                          </div>
                        </div>
                      </td>

                      {/* Quotes & Assigned Professional */}
                      <td className="py-3.5 px-4">
                        {isAssigned ? (
                          <div className="space-y-1">
                            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-blue-500/20 text-blue-300 border border-blue-500/30 text-[11px] font-bold">
                              <UserCheck className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                              <span className="truncate">{assignedName || 'Assigned Professional'}</span>
                            </div>
                            <div className="text-[10px] text-slate-400 flex items-center gap-2">
                              {job.assignedProfessionalPhone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3 text-slate-500" />
                                  <span>{job.assignedProfessionalPhone}</span>
                                </span>
                              )}
                              {job.agreedAmount && (
                                <span className="text-emerald-400 font-mono">
                                  ₦{Number(job.agreedAmount).toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-0.5">
                            <span className="font-bold text-white text-xs">
                              {job.quotesCount || (job.quotes ? job.quotes.length : 0)} Quotes
                            </span>
                            <div className="text-[10px] text-slate-400 flex items-center gap-1">
                              <span>{job.matchesCount || 0} Matched Artisans</span>
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Pipeline Status */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1">
                          <span
                            className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                              job.status === 'open'
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                : job.status === 'in_progress' || job.pipelineStage === 'in_progress'
                                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                                : job.status === 'professional_completed' || job.pipelineStage === 'submitted'
                                ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                                : job.status === 'assigned' || isAssigned
                                ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                                : job.status === 'completed'
                                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                : 'bg-slate-800 text-slate-400 border border-slate-700'
                            }`}
                          >
                            {job.pipelineStage === 'submitted'
                              ? 'Evidence Submitted'
                              : job.status === 'in_progress' || job.pipelineStage === 'in_progress'
                              ? 'In Progress'
                              : job.status === 'assigned'
                              ? 'Assigned • Pending Start'
                              : job.status}
                          </span>

                          {isFlagged && (
                            <div className="text-[10px] text-amber-400 font-medium truncate max-w-xs">
                              Flagged: {job.flagReason || 'Review'}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Inspect Modal */}
                          <button
                            onClick={() => setSelectedJob(job)}
                            className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                            title="Inspect Job & AI Scope Details"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>

                          {/* Reassign / Assign Button */}
                          <button
                            onClick={() => handleOpenActionModal(job, isAssigned ? 'reassign' : 'assign')}
                            className="px-2 py-1 bg-blue-500/20 hover:bg-blue-500 text-blue-300 hover:text-slate-950 rounded-lg text-xs font-bold border border-blue-500/30 transition-all flex items-center gap-1"
                            title={isAssigned ? 'Reassign Professional' : 'Assign Professional'}
                          >
                            <UserCheck className="w-3 h-3" />
                            <span>{isAssigned ? 'Reassign' : 'Assign'}</span>
                          </button>

                          {/* Unassign Button (Reverts to open pool) */}
                          {isAssigned && job.status !== 'completed' && job.status !== 'cancelled' && (
                            <button
                              onClick={() => handleOpenActionModal(job, 'unassign')}
                              className="px-2 py-1 bg-slate-800 hover:bg-rose-500 text-slate-400 hover:text-white rounded-lg text-xs font-semibold border border-slate-700 transition-all"
                              title="Unassign Professional (Return to Open Pool)"
                            >
                              Unassign
                            </button>
                          )}

                          {/* Flag / Unflag */}
                          {isFlagged ? (
                            <button
                              onClick={() => handleOpenActionModal(job, 'unflag')}
                              className="px-2 py-1 bg-amber-500/20 hover:bg-amber-500 text-amber-300 hover:text-slate-950 rounded-lg text-xs font-bold border border-amber-500/30 transition-all"
                            >
                              Unflag
                            </button>
                          ) : (
                            <button
                              onClick={() => handleOpenActionModal(job, 'flag')}
                              className="p-1.5 bg-slate-800 hover:bg-amber-900/40 text-slate-400 hover:text-amber-300 rounded-lg transition-colors"
                              title="Flag Job for Review"
                            >
                              <Flag className="w-3.5 h-3.5" />
                            </button>
                          )}

                          {/* Cancel Job */}
                          {job.status !== 'cancelled' && (
                            <button
                              onClick={() => handleOpenActionModal(job, 'cancel')}
                              className="p-1.5 bg-slate-800 hover:bg-rose-900/40 text-slate-400 hover:text-rose-300 rounded-lg transition-colors"
                              title="Cancel Job"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Job Details Modal */}
      {selectedJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl p-6 sm:p-8 space-y-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    {selectedJob.pipelineStage || selectedJob.status}
                  </span>
                  <span className="text-xs text-slate-400 font-mono">ID: {selectedJob.id}</span>
                </div>
                <h3 className="text-lg font-bold text-white mt-1.5">{selectedJob.title}</h3>
                <p className="text-xs text-slate-400">
                  Posted by <strong className="text-slate-200">{selectedJob.customerName || 'Customer'}</strong> • {selectedJob.lga ? `${selectedJob.lga}, ` : ''}{selectedJob.state || 'Lagos'}
                </p>
              </div>

              <button
                onClick={() => setSelectedJob(null)}
                className="p-1.5 hover:bg-slate-800 rounded-xl text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            {/* Authoritative Assignment Card if Assigned */}
            {(selectedJob.assignedProfessionalId || selectedJob.selectedProfessionalId) && (
              <div className="bg-blue-950/20 border border-blue-500/30 rounded-2xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-blue-400 text-xs font-bold uppercase tracking-wider">
                    <UserCheck className="w-4 h-4" />
                    <span>Confirmed Assigned Professional</span>
                  </div>
                  <button
                    onClick={() => {
                      const job = selectedJob;
                      setSelectedJob(null);
                      handleOpenActionModal(job, 'reassign');
                    }}
                    className="text-[11px] font-bold text-blue-400 hover:text-blue-300 underline"
                  >
                    Reassign
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-400">Professional Name:</span>
                    <p className="text-white font-semibold">
                      {selectedJob.assignedProfessionalName || selectedJob.selectedProfessionalName}
                    </p>
                  </div>
                  {selectedJob.assignedProfessionalBusiness && (
                    <div>
                      <span className="text-slate-400">Business / Enterprise:</span>
                      <p className="text-slate-200">{selectedJob.assignedProfessionalBusiness}</p>
                    </div>
                  )}
                  {selectedJob.assignedProfessionalPhone && (
                    <div>
                      <span className="text-slate-400">Phone:</span>
                      <p className="text-slate-200">{selectedJob.assignedProfessionalPhone}</p>
                    </div>
                  )}
                  {selectedJob.assignedAt && (
                    <div>
                      <span className="text-slate-400">Assigned At:</span>
                      <p className="text-slate-200">{new Date(selectedJob.assignedAt).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                Customer Problem Description:
              </h4>
              <p className="text-xs text-slate-200 leading-relaxed whitespace-pre-wrap">
                {selectedJob.description}
              </p>
            </div>

            {/* Gemini AI Scope Analysis Card */}
            {selectedJob.aiAnalysis && (
              <div className="bg-emerald-950/20 border border-emerald-500/30 rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-2 text-emerald-400">
                  <Sparkles className="w-4 h-4" />
                  <h4 className="text-xs font-bold uppercase tracking-wider">
                    Gemini AI Scope & Nigerian Cost Analysis
                  </h4>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-slate-400">Detected Trade Category:</span>
                    <p className="text-emerald-300 font-semibold">{selectedJob.aiAnalysis.detected_category}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Job Complexity:</span>
                    <p className="text-white font-medium capitalize">{selectedJob.aiAnalysis.estimated_job_complexity || 'Moderate'}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-slate-400">Summary of Problem:</span>
                    <p className="text-slate-300">{selectedJob.aiAnalysis.problem_summary}</p>
                  </div>
                  {selectedJob.aiAnalysis.required_skills && (
                    <div className="sm:col-span-2">
                      <span className="text-slate-400">Required Skills / Tools:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedJob.aiAnalysis.required_skills.map((skill: string, i: number) => (
                          <span key={i} className="px-2 py-0.5 bg-slate-800 rounded text-slate-300 text-[11px]">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Modal Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-slate-800">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const job = selectedJob;
                    setSelectedJob(null);
                    handleOpenActionModal(job, 'update_status');
                  }}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl"
                >
                  Change Status
                </button>

                {(selectedJob.assignedProfessionalId || selectedJob.selectedProfessionalId) && (
                  <button
                    onClick={() => {
                      const job = selectedJob;
                      setSelectedJob(null);
                      handleOpenActionModal(job, 'unassign');
                    }}
                    className="px-3 py-1.5 bg-rose-500/20 hover:bg-rose-500 text-rose-300 hover:text-slate-950 text-xs font-semibold rounded-xl transition-all"
                  >
                    Unassign Professional
                  </button>
                )}
              </div>

              <button
                onClick={() => setSelectedJob(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation & Reassignment Modal */}
      {actionModal.isOpen && actionModal.job && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-md shadow-2xl p-6 space-y-4">
            <h3 className="text-base font-bold text-white capitalize flex items-center gap-2">
              {actionModal.action === 'reassign' || actionModal.action === 'assign' ? (
                <UserCheck className="w-5 h-5 text-blue-400" />
              ) : (
                <Flag className="w-5 h-5 text-amber-400" />
              )}
              <span>{actionModal.action.replace('_', ' ')}: {actionModal.job.title}</span>
            </h3>

            {/* Reassign / Assign Professional Selector */}
            {(actionModal.action === 'assign' || actionModal.action === 'reassign') && (
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                  Select Verified Professional:
                </label>
                <select
                  value={selectedProId}
                  onChange={(e) => setSelectedProId(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">-- Choose verified artisan --</option>
                  {eligibleProsForAssignment.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.fullName || p.full_name} ({p.primaryCategory || p.primary_category || 'Artisan'}) • {p.lga || ''}, {p.state || 'Lagos'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Update Status Selector */}
            {actionModal.action === 'update_status' && (
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                  Select New Status:
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-xs text-white"
                >
                  <option value="open">Open (Allow quotes)</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                Reason / Administrative Note:
              </label>
              <textarea
                rows={3}
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Enter justification..."
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setActionModal({ isOpen: false, job: null, action: 'flag' })}
                disabled={isProcessing}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={
                  isProcessing ||
                  !actionReason.trim() ||
                  ((actionModal.action === 'assign' || actionModal.action === 'reassign') && !selectedProId)
                }
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 text-xs font-bold rounded-xl transition-colors disabled:opacity-50"
              >
                {isProcessing ? 'Processing...' : 'Confirm Action'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
